import { useState } from 'react';
import { AlertTriangle } from 'lucide-react';

import { useTranslation } from '../../i18n';
import { useBanUser } from '../../hooks/queries/useUserQueries';
import { IUser } from '../../types';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';

interface IUserBanDialogProps {
  isOpen: boolean;
  onClose: () => void;
  user: IUser | null;
}

const UserBanDialog = ({ isOpen, onClose, user }: IUserBanDialogProps) => {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const banUser = useBanUser();

  const handleConfirm = async () => {
    if (!user) return;

    await banUser.mutateAsync({ userId: user.id, reason });
    setReason('');
    onClose();
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setReason('');
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>{t('users.ban_title')}</DialogTitle>
          <DialogDescription>
            {t('users.ban_confirm', { name: user?.displayName || '' })}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-medium text-red-900">
                  {t('users.ban_warning_title')}
                </p>
                <p className="text-sm text-red-700 mt-1">
                  {t('users.ban_warning')}
                </p>
              </div>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('users.ban_reason')}
            </label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('users.ban_reason_placeholder')}
              rows={3}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={banUser.isPending}
          >
            {banUser.isPending ? t('common.saving') : t('users.confirm_ban')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UserBanDialog;
